import { useState, useEffect } from "react";
import { api, errMsg } from "../../lib/api";
import { toast } from "sonner";
import { DollarSign, Users, GraduationCap, RotateCcw } from "lucide-react";

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <p className="text-[var(--forge-dim)]">Loading…</p>;
  const stats = [
    ["Revenue (USD)", `$${(d.revenue.USD || 0).toFixed(2)}`, DollarSign, "forge-green"],
    ["Revenue (EUR)", `€${(d.revenue.EUR || 0).toFixed(2)}`, DollarSign, "forge-green"],
    ["Active enrollments", d.enrollments, GraduationCap, "forge-blue"],
    ["Students", d.students, Users, "forge-blue"],
    ["Pending refunds", d.pending_refunds, RotateCcw, "forge-gold"],
    ["Total orders", d.total_orders, DollarSign, "forge-blue"],
  ];
  return (
    <div>
      <h1 className="font-display font-bold text-xl mb-6" data-testid="admin-dashboard-title">DASHBOARD</h1>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {stats.map(([label, val, Icon, color]) => (
          <div key={label} className="forge-card !transform-none p-4">
            <div className="flex items-center gap-2 text-[var(--forge-dim)] text-xs mb-1"><Icon size={13} />{label}</div>
            <p className={`font-display font-bold text-2xl text-[var(--${color})]`} data-testid={`stat-${label.replace(/[^a-z]/gi, "-").toLowerCase()}`}>{val}</p>
          </div>
        ))}
      </div>
      <h2 className="font-display font-bold text-base mb-3">REVENUE BY COURSE</h2>
      <div className="forge-card !transform-none divide-y divide-[var(--forge-border)] mb-8">
        {d.by_course.length === 0 && <p className="p-4 text-sm text-[var(--forge-dim)]">No sales yet.</p>}
        {d.by_course.map((c) => (
          <div key={c.course_id} className="flex justify-between p-3 text-sm">
            <span>{c.title}</span>
            <span className="text-[var(--forge-dim)]">{c.sales} sales · <span className="text-[var(--forge-green)]">${c.revenue_usd.toFixed(2)}</span></span>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <h2 className="font-display font-bold text-base mb-3">REVENUE BY MONTH</h2>
          <div className="forge-card !transform-none p-4" data-testid="revenue-by-month">
            {(d.by_month || []).length === 0 && <p className="text-sm text-[var(--forge-dim)]">No sales yet.</p>}
            {(d.by_month || []).map((m) => {
              const max = Math.max(...d.by_month.map((x) => x.revenue_usd), 1);
              return (
                <div key={m.month} className="mb-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-display">{m.month}</span>
                    <span className="text-[var(--forge-dim)]">{m.orders} orders · <span className="text-[var(--forge-green)]">${m.revenue_usd.toFixed(2)}</span></span>
                  </div>
                  <div className="h-2 rounded" style={{ background: "var(--forge-border)" }}>
                    <div className="h-2 rounded" style={{ width: `${(m.revenue_usd / max) * 100}%`, background: "linear-gradient(90deg,var(--forge-blue),var(--forge-green))" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <h2 className="font-display font-bold text-base mb-3">TOP COUPONS</h2>
          <div className="forge-card !transform-none divide-y divide-[var(--forge-border)]" data-testid="top-coupons">
            {(d.top_coupons || []).length === 0 && <p className="p-4 text-sm text-[var(--forge-dim)]">No coupon redemptions yet.</p>}
            {(d.top_coupons || []).map((cp) => (
              <div key={cp.code} className="flex justify-between p-3 text-sm">
                <span className="font-display" style={{ color: "var(--forge-gold)", letterSpacing: 1 }}>{cp.code}</span>
                <span className="text-[var(--forge-dim)]">{cp.uses} use{cp.uses !== 1 ? "s" : ""} · <span className="text-[var(--forge-green)]">${cp.revenue_usd.toFixed(2)}</span></span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <h2 className="font-display font-bold text-base mb-3 mt-8">INSTRUCTOR PROFILE (public page: /instructor)</h2>
      <ProfileEditor />
      <DangerZone />
    </div>
  );
}

function DangerZone() {
  const [busy, setBusy] = useState(false);
  const reset = async () => {
    if (!window.confirm("⚠ This permanently deletes ALL students, orders, refunds, reviews, Q&A, certificates, progress and unused media videos.\n\nCourses, bundles, coupons and your admin account are kept.\n\nAre you absolutely sure?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-demo-data");
      toast.success(`Cleaned: ${data.students} students, ${data.orders} orders, ${data.refunds} refunds, ${data.reviews} reviews, ${data.media} unused videos`);
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="forge-card !transform-none p-4 mt-8" style={{ borderColor: "rgba(255,90,90,.35)" }} data-testid="danger-zone">
      <h2 className="font-display font-bold text-sm mb-1" style={{ color: "#ff6b6b" }}>DANGER ZONE</h2>
      <p className="text-xs mb-3" style={{ color: "var(--muted)" }}>
        Remove all test/demo data: students, orders, refunds, reviews, Q&A, certificates and unused media videos.
        Your courses, bundles, coupons and admin account stay untouched. This cannot be undone.
      </p>
      <button className="btn-ghost !py-2 text-sm" style={{ borderColor: "rgba(255,90,90,.5)", color: "#ff6b6b" }}
        disabled={busy} onClick={reset} data-testid="reset-demo-data-btn">
        {busy ? "CLEANING…" : "🧹 RESET STUDENT & SALES DATA"}
      </button>
    </div>
  );
}

function ProfileEditor() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/instructor").then((r) => setP(r.data)).catch(() => {}); }, []);
  if (!p) return null;
  const save = async () => {
    try {
      await api.put("/admin/instructor", { name: p.name, headline: p.headline, bio: p.bio,
        youtube: p.youtube || "", twitter: p.twitter || "", discord: p.discord || "" });
      toast.success("Profile saved ✓");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="forge-card !transform-none p-4" data-testid="instructor-profile-editor">
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="text-xs text-[var(--forge-dim)]">Display name</label>
          <input data-testid="profile-name-input" value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} className="input-dark text-sm mt-1" /></div>
        <div><label className="text-xs text-[var(--forge-dim)]">Headline</label>
          <input data-testid="profile-headline-input" value={p.headline} onChange={(e) => setP({ ...p, headline: e.target.value })} className="input-dark text-sm mt-1" /></div>
      </div>
      <div className="mt-3"><label className="text-xs text-[var(--forge-dim)]">Bio</label>
        <textarea data-testid="profile-bio-input" rows={3} value={p.bio} onChange={(e) => setP({ ...p, bio: e.target.value })} className="input-dark text-sm mt-1" /></div>
      <div className="grid sm:grid-cols-3 gap-3 mt-3">
        <input data-testid="profile-youtube-input" value={p.youtube || ""} onChange={(e) => setP({ ...p, youtube: e.target.value })} placeholder="YouTube URL" className="input-dark text-sm" />
        <input data-testid="profile-twitter-input" value={p.twitter || ""} onChange={(e) => setP({ ...p, twitter: e.target.value })} placeholder="X / Twitter URL" className="input-dark text-sm" />
        <input data-testid="profile-discord-input" value={p.discord || ""} onChange={(e) => setP({ ...p, discord: e.target.value })} placeholder="Discord invite URL" className="input-dark text-sm" />
      </div>
      <button className="btn-primary !py-2 text-sm mt-4" data-testid="profile-save-btn" onClick={save}>SAVE PROFILE</button>
    </div>
  );
}
